/**
 * Intended uses and the attestations they request, derived from the
 * registration certificates in the replayed log (Annex I points 9 and 10 of
 * (EU) 2025/848).
 *
 * Certificates are grouped into intended uses by the same identifier as the
 * registrar (see intendedUseId), so the result can be compared one-to-one with
 * the register's /intended-uses. Payloads are read defensively: a published
 * certificate stays part of the register even if it does not follow the
 * schema.
 */

import {
  CertificateRecord,
  LogReplay,
  compareSequence,
} from "./transparency-log";

export interface LocalizedText {
  lang?: string;
  value: string;
}

export interface RequestedClaim {
  path: Array<string | number | null>;
  /** Dot-joined path with null rendered as "*", as in the register API. */
  normalizedPath: string;
  values?: unknown[];
}

export interface RequestedCredential {
  format: string;
  /** vct_values (SD-JWT VC) or doctype_value (mdoc); empty when missing. */
  types: string[];
  /** Undefined when the registration declares no claims for the credential. */
  claims?: RequestedClaim[];
}

export interface IntendedUse {
  id: string;
  wrpId: string;
  purpose: LocalizedText[];
  privacyPolicy?: string;
  credentials: RequestedCredential[];
  provides: Array<{ format: string; types: string[] }>;
  /** Every registration certificate of the intended use, oldest first. */
  certificates: CertificateRecord[];
  /** The certificate the content is read from: latest unrevoked, else latest. */
  latest: CertificateRecord;
  /** True while at least one of its certificates is unrevoked. */
  current: boolean;
}

export interface ClaimSummary {
  path: string;
  wrpIds: Set<string>;
  uses: IntendedUse[];
}

export interface AttestationTypeSummary {
  /** vct or doctype; the empty string collects credentials without a type. */
  type: string;
  formats: string[];
  wrpIds: Set<string>;
  uses: IntendedUse[];
  /** Most requested first. */
  claims: ClaimSummary[];
  /** Intended uses that request the type without declaring claims. */
  undeclared: IntendedUse[];
}

export function deriveIntendedUses(replayed: LogReplay): IntendedUse[] {
  const groups = new Map<string, CertificateRecord[]>();
  for (const record of replayed.certificates.values()) {
    if (!record.registration || !record.intendedUseId) continue;
    const key = `${record.wrpId}\n${record.intendedUseId}`;
    groups.set(key, [...(groups.get(key) ?? []), record]);
  }

  return [...groups.values()].map((group) => {
    const certificates = group.sort((left, right) =>
      compareSequence(
        left.issued.sequence_number,
        right.issued.sequence_number,
      ),
    );
    const latest =
      [...certificates].reverse().find((record) => !record.revoked) ??
      certificates[certificates.length - 1];
    const payload = latest.registration!;
    return {
      id: latest.intendedUseId!,
      wrpId: latest.wrpId,
      purpose: readPurpose(payload.purpose),
      privacyPolicy:
        typeof payload.privacy_policy === "string"
          ? payload.privacy_policy
          : undefined,
      credentials: readCredentials(payload.credentials),
      provides: readList(payload.provides_attestations).map((entry) => ({
        format: stringOr(entry["format"], "unknown"),
        types: attestationTypes(entry["meta"]),
      })),
      certificates,
      latest,
      current: certificates.some((record) => !record.revoked),
    };
  });
}

/** Groups intended uses by every attestation type they request. */
export function indexByAttestationType(
  uses: IntendedUse[],
): AttestationTypeSummary[] {
  const summaries = new Map<
    string,
    {
      formats: Set<string>;
      uses: Map<string, IntendedUse>;
      claims: Map<string, Map<string, IntendedUse>>;
      undeclared: Map<string, IntendedUse>;
    }
  >();

  for (const use of uses) {
    for (const credential of use.credentials) {
      for (const type of credential.types.length ? credential.types : [""]) {
        let summary = summaries.get(type);
        if (!summary) {
          summary = {
            formats: new Set(),
            uses: new Map(),
            claims: new Map(),
            undeclared: new Map(),
          };
          summaries.set(type, summary);
        }
        const key = `${use.wrpId}\n${use.id}`;
        summary.formats.add(credential.format);
        summary.uses.set(key, use);
        if (!credential.claims) {
          summary.undeclared.set(key, use);
        }
        for (const claim of credential.claims ?? []) {
          const users = summary.claims.get(claim.normalizedPath) ?? new Map();
          users.set(key, use);
          summary.claims.set(claim.normalizedPath, users);
        }
      }
    }
  }

  return [...summaries.entries()]
    .map(([type, summary]) => {
      const typeUses = [...summary.uses.values()];
      return {
        type,
        formats: [...summary.formats].sort(),
        wrpIds: new Set(typeUses.map((use) => use.wrpId)),
        uses: typeUses,
        claims: [...summary.claims.entries()]
          .map(([path, users]) => {
            const claimUses = [...users.values()];
            return {
              path,
              wrpIds: new Set(claimUses.map((use) => use.wrpId)),
              uses: claimUses,
            };
          })
          .sort(
            (left, right) =>
              right.wrpIds.size - left.wrpIds.size ||
              left.path.localeCompare(right.path),
          ),
        undeclared: [...summary.undeclared.values()],
      };
    })
    .sort(
      (left, right) =>
        right.wrpIds.size - left.wrpIds.size ||
        left.type.localeCompare(right.type),
    );
}

/** The credentials of an intended use that request the given type. */
export function credentialsOfType(
  use: IntendedUse,
  type: string,
): RequestedCredential[] {
  return use.credentials.filter((credential) =>
    type ? credential.types.includes(type) : credential.types.length === 0,
  );
}

/** Purpose in the preferred language, else the first one. */
export function purposeText(
  purpose: LocalizedText[],
  preferred = "en",
): string | undefined {
  return (
    purpose.find((entry) => entry.lang?.toLowerCase().startsWith(preferred)) ??
    purpose[0]
  )?.value;
}

/**
 * TS 119 475 uses { lang, value }. Older certificates in the log use
 * { locale, name } or { lang, content }; they are shown, not corrected.
 */
export function readPurpose(value: unknown): LocalizedText[] {
  return readList(value).flatMap((entry) => {
    const text = entry["value"] ?? entry["name"] ?? entry["content"];
    const lang = entry["lang"] ?? entry["locale"];
    return typeof text === "string"
      ? [{ lang: typeof lang === "string" ? lang : undefined, value: text }]
      : [];
  });
}

export function readCredentials(value: unknown): RequestedCredential[] {
  return readList(value).map((entry) => {
    const claims = readList(entry["claim"]).flatMap((claim) => {
      const path = claim["path"];
      if (!Array.isArray(path) || path.length === 0) return [];
      return [
        {
          path: path as RequestedClaim["path"],
          normalizedPath: path
            .map((segment) => (segment === null ? "*" : String(segment)))
            .join("."),
          values: Array.isArray(claim["values"]) ? claim["values"] : undefined,
        },
      ];
    });
    return {
      format: stringOr(entry["format"], "unknown"),
      types: attestationTypes(entry["meta"]),
      claims: claims.length ? claims : undefined,
    };
  });
}

/** vct_values, vct or doctype_value of a credential's meta object. */
export function attestationTypes(meta: unknown): string[] {
  if (typeof meta !== "object" || meta === null) return [];
  const record = meta as Record<string, unknown>;
  const vct = record["vct_values"] ?? record["vct"];
  if (Array.isArray(vct)) {
    return vct.filter((value): value is string => typeof value === "string");
  }
  if (typeof vct === "string") return [vct];
  return typeof record["doctype_value"] === "string"
    ? [record["doctype_value"]]
    : [];
}

function readList(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value)
    ? value.filter(
        (entry): entry is Record<string, unknown> =>
          typeof entry === "object" && entry !== null && !Array.isArray(entry),
      )
    : [];
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === "string" && value ? value : fallback;
}
